import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Cog, Loader2, QrCode } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  running: { label: "Em Execução", color: "bg-green-500 text-white" },
  idle: { label: "Parada", color: "bg-amber-500 text-white" },
  stopped: { label: "Desligada", color: "bg-red-500 text-white" },
  maintenance: { label: "Manutenção", color: "bg-purple-500 text-white" },
  setup: { label: "Setup", color: "bg-blue-500 text-white" },
};

const MACHINE_TYPES = [
  { value: "injection", label: "Injeção" },
  { value: "extrusion", label: "Extrusão" },
  { value: "blow", label: "Sopro" },
  { value: "tampografia", label: "Tampografia" },
  { value: "serigrafia", label: "Serigrafia" },
  { value: "montagem", label: "Montagem" },
];

export default function Machines() {
  const { user } = useAuth();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    machine_type: "injection",
    tonnage: "",
    location: "",
    nominal_capacity: "",
    average_efficiency: "85",
    planned_hours_per_day: "24",
    operational_days_per_week: "7",
    status: "idle",
  });

  const isAdmin = user?.role === "admin";
  const canChangeStatus = ["admin", "operator", "shift_leader"].includes(user?.role);

  const fetchMachines = async () => {
    try {
      const response = await axios.get(`${API}/machines`);
      setMachines(response.data);
    } catch (error) {
      toast.error("Erro ao carregar máquinas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMachines();
  }, []);

  const resetForm = () => {
    setFormData({
      code: "",
      description: "",
      machine_type: "injection",
      tonnage: "",
      location: "",
      nominal_capacity: "",
      average_efficiency: "85",
      planned_hours_per_day: "24",
      operational_days_per_week: "7",
      status: "idle",
    });
    setEditingMachine(null);
  };

  const handleOpenDialog = (machine = null) => {
    if (machine) {
      setEditingMachine(machine);
      setFormData({
        code: machine.code,
        description: machine.description,
        machine_type: machine.machine_type || "injection",
        tonnage: machine.tonnage.toString(),
        location: machine.location || "",
        nominal_capacity: machine.nominal_capacity?.toString() || "",
        average_efficiency: (machine.average_efficiency * 100).toString(),
        planned_hours_per_day: (machine.planned_hours_per_day ?? 24).toString(),
        operational_days_per_week: (machine.operational_days_per_week ?? 7).toString(),
        status: machine.status,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      code: formData.code,
      description: formData.description,
      machine_type: formData.machine_type,
      tonnage: parseInt(formData.tonnage),
      location: formData.location || null,
      nominal_capacity: formData.nominal_capacity ? parseInt(formData.nominal_capacity) : null,
      average_efficiency: parseFloat(formData.average_efficiency) / 100,
      planned_hours_per_day: parseFloat(formData.planned_hours_per_day) || 24,
      operational_days_per_week: parseInt(formData.operational_days_per_week) || 7,
      status: formData.status,
    };

    try {
      if (editingMachine) {
        await axios.put(`${API}/machines/${editingMachine.id}`, payload);
        toast.success("Máquina atualizada com sucesso");
      } else {
        await axios.post(`${API}/machines`, payload);
        toast.success("Máquina criada com sucesso");
      }
      setDialogOpen(false);
      resetForm();
      fetchMachines();
    } catch (error) {
      showError(error, "Erro ao guardar máquina");
    }
  };

  const handleDelete = async (machineId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar esta máquina?")) return;
    
    try {
      await axios.delete(`${API}/machines/${machineId}`);
      toast.success("Máquina eliminada");
      fetchMachines();
    } catch (error) {
      showError(error, "Erro ao eliminar máquina");
    }
  };

  const handleStatusChange = async (machineId, newStatus) => {
    try {
      await axios.patch(`${API}/machines/${machineId}/status?status=${newStatus}`);
      toast.success("Estado atualizado");
      fetchMachines();
    } catch (error) {
      showError(error, "Erro ao atualizar estado");
    }
  };

  const printMachineQrs = () => {
    if (!machines?.length) { toast.error("Sem máquinas para imprimir"); return; }
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup bloqueado — permita popups"); return; }
    const origin = window.location.origin;
    const cards = machines.map(m => {
      const url = `${origin}/operator-panel?machine=${encodeURIComponent(m.code)}`;
      const qr = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(url)}&margin=2`;
      return `
        <div class="card">
          <div class="header">EP3D — MÁQUINA</div>
          <div class="code">${m.code}</div>
          <div class="desc">${(m.description || "").replace(/</g,"&lt;")}</div>
          <img src="${qr}" alt="QR" />
          <div class="hint">Aponte a câmara para abrir o painel desta máquina</div>
          ${m.model ? `<div class="model">Modelo: ${m.model}${m.tonnage ? " · " + m.tonnage + " T" : ""}</div>` : ""}
        </div>`;
    }).join("");
    w.document.write(`
      <!DOCTYPE html><html><head><meta charset="utf-8"><title>QR das Máquinas — EP3D</title>
      <style>
        @page { size: A4; margin: 10mm; }
        body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
        .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8mm; }
        .card { border: 2px solid #0f172a; border-radius: 8px; padding: 8mm; text-align: center; page-break-inside: avoid; background: #fff; }
        .header { font-size: 11px; letter-spacing: 3px; color: #64748b; font-weight: bold; }
        .code { font-size: 42px; font-weight: 900; color: #0f172a; margin: 4px 0; letter-spacing: 2px; }
        .desc { font-size: 14px; color: #334155; margin-bottom: 8mm; }
        .card img { width: 55mm; height: 55mm; }
        .hint { font-size: 10px; color: #64748b; margin-top: 6mm; font-style: italic; }
        .model { font-size: 11px; color: #475569; margin-top: 3mm; }
        @media print { .no-print { display: none; } }
      </style></head><body>
        <div class="no-print" style="text-align:center;padding:10px;background:#f1f5f9;">
          <button onclick="window.print()" style="padding:8px 24px;font-size:16px;background:#2563eb;color:white;border:none;border-radius:4px;cursor:pointer">Imprimir</button>
        </div>
        <div class="grid">${cards}</div>
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 800);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="machines-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Gestão de Máquinas
          </h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin ? "Configure as máquinas de injeção" : "Altere o estado das máquinas"}
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={printMachineQrs} data-testid="print-qr-machines-btn">
              <QrCode className="h-4 w-4 mr-2" />
              Imprimir QR das Máquinas
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => handleOpenDialog()} data-testid="add-machine-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Máquina
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-chivo text-foreground">
                  {editingMachine ? "Editar Máquina" : "Nova Máquina"}
                </DialogTitle>
                <DialogDescription>
                  Preencha os dados da máquina
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Código *</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="Ex: INJ-001"
                      required
                      data-testid="machine-code"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="machine_type">Tipo *</Label>
                    <select
                      id="machine_type"
                      value={formData.machine_type}
                      onChange={(e) => setFormData({ ...formData, machine_type: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="machine-type"
                    >
                      {MACHINE_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Descrição *</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Ex: Engel Victory 200"
                    required
                    data-testid="machine-description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tonnage">Tonelagem *</Label>
                    <Input
                      id="tonnage"
                      type="number"
                      min="1"
                      value={formData.tonnage}
                      onChange={(e) => setFormData({ ...formData, tonnage: e.target.value })}
                      placeholder="200"
                      required
                      data-testid="machine-tonnage"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Localização</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Ex: Nave 1"
                      data-testid="machine-location"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="planned_hours_per_day">Horas Planeadas / Dia (OEE)</Label>
                    <Input
                      id="planned_hours_per_day"
                      type="number"
                      min="0"
                      max="24"
                      step="0.5"
                      value={formData.planned_hours_per_day}
                      onChange={(e) => setFormData({ ...formData, planned_hours_per_day: e.target.value })}
                      placeholder="24"
                      data-testid="machine-planned-hours"
                    />
                    <p className="text-[10px] text-slate-500">Tempo planeado de funcionamento por dia para calculo OEE</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="operational_days_per_week">Dias Operacionais / Semana</Label>
                    <Input
                      id="operational_days_per_week"
                      type="number"
                      min="1"
                      max="7"
                      value={formData.operational_days_per_week}
                      onChange={(e) => setFormData({ ...formData, operational_days_per_week: e.target.value })}
                      placeholder="7"
                      data-testid="machine-operational-days"
                    />
                    <p className="text-[10px] text-slate-500">Quantos dias por semana esta maquina opera</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="average_efficiency">Eficiência (%)</Label>
                    <Input
                      id="average_efficiency"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.average_efficiency}
                      onChange={(e) => setFormData({ ...formData, average_efficiency: e.target.value })}
                      placeholder="85"
                      data-testid="machine-efficiency"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Estado</Label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="machine-status"
                    >
                      <option value="idle">Parada</option>
                      <option value="running">Em Execução</option>
                      <option value="stopped">Desligada</option>
                      <option value="maintenance">Manutenção</option>
                      <option value="setup">Setup</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" data-testid="save-machine-btn">
                    {editingMachine ? "Guardar Alterações" : "Criar Máquina"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Table */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-chivo flex items-center gap-2 text-foreground">
            <Cog className="h-5 w-5" />
            Lista de Máquinas ({machines.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {machines.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Tonelagem</TableHead>
                    <TableHead>Localização</TableHead>
                    <TableHead>Eficiência</TableHead>
                    <TableHead>Estado</TableHead>
                    {isAdmin && <TableHead className="text-right">Ações</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {machines.map((machine) => {
                    const statusConfig = STATUS_CONFIG[machine.status] || STATUS_CONFIG.idle;
                    const typeLabel = MACHINE_TYPES.find(t => t.value === machine.machine_type)?.label || machine.machine_type;
                    
                    return (
                      <TableRow key={machine.id} data-testid={`machine-row-${machine.code}`}>
                        <TableCell className="font-mono font-bold text-foreground">{machine.code}</TableCell>
                        <TableCell className="text-foreground">{machine.description}</TableCell>
                        <TableCell className="text-foreground">{typeLabel}</TableCell>
                        <TableCell className="font-mono text-foreground">{machine.tonnage}T</TableCell>
                        <TableCell className="text-foreground">{machine.location || "-"}</TableCell>
                        <TableCell className="font-mono text-foreground">
                          {(machine.average_efficiency * 100).toFixed(0)}%
                        </TableCell>
                        <TableCell>
                          {canChangeStatus ? (
                            <select
                              value={machine.status}
                              onChange={(e) => handleStatusChange(machine.id, e.target.value)}
                              className={`h-8 rounded-md border-0 px-2 text-xs font-medium ${statusConfig.color}`}
                              data-testid={`status-select-${machine.code}`}
                            >
                              <option value="idle" className="bg-white text-gray-900">Parada</option>
                              <option value="running" className="bg-white text-gray-900">Em Execução</option>
                              <option value="stopped" className="bg-white text-gray-900">Desligada</option>
                              <option value="maintenance" className="bg-white text-gray-900">Manutenção</option>
                              <option value="setup" className="bg-white text-gray-900">Setup</option>
                            </select>
                          ) : (
                            <Badge className={statusConfig.color}>
                              {statusConfig.label}
                            </Badge>
                          )}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenDialog(machine)}
                                data-testid={`edit-machine-${machine.code}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(machine.id)}
                                className="text-destructive hover:text-destructive"
                                data-testid={`delete-machine-${machine.code}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <Cog className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Nenhuma máquina registada</p>
              {isAdmin && <p className="text-sm">Clique em "Nova Máquina" para começar</p>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
